import { useCallback, useRef, useState } from 'react';

export const ALL_OPTION_KEY = 'all' as const;

type OptionValue = string | number | null;
export type AllOptionKey = typeof ALL_OPTION_KEY;
export type SelectOptionType<V extends OptionValue = OptionValue> = {
  value: V;
  display?: string | null;
};

function useMultipleOptions<T extends SelectOptionType>(
  initialOption: T,
  treatAsSeparate: OptionValue[] = [ALL_OPTION_KEY],
) {
  const [selectedOptions, setSelectedOptions] = useState<T[]>([initialOption]);
  const selectedOptionsRef = useRef(selectedOptions);
  selectedOptionsRef.current = selectedOptions;
  const treatAsSeparateRef = useRef(treatAsSeparate);
  treatAsSeparateRef.current = treatAsSeparate;

  const handleSelected = useCallback(
    (options: T[]) => {
      if (!options) return;
      if (options.length === 0) {
        setSelectedOptions([initialOption]);
        return;
      }
      if (
        selectedOptionsRef.current.length === 1 &&
        // @ts-ignore
        treatAsSeparateRef.current.includes(selectedOptionsRef.current[0]?.value)
      ) {
        options.splice(0, 1);
      } else if (options.length > 1) {
        const index = options.findIndex((option) =>
          // @ts-ignore
          treatAsSeparateRef.current.includes(option?.value),
        );
        if (index !== -1) {
          options = [options[index]];
        }
      }
      setSelectedOptions(options);
    },
    [initialOption, selectedOptionsRef, treatAsSeparateRef],
  );

  const reset = () => setSelectedOptions([initialOption]);

  return { selected: selectedOptions, handleSelected, setSelectedOptions, reset };
}

export default useMultipleOptions;
